/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import { Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";

/* Component Imports */

import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Styled Components */

const Container = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: "2rem",
	gap: "1.5rem",
}));

const SummaryCard = styled("div")(() => ({
	border: "1px solid #e0e0e0",
	borderRadius: "0.75rem",
	padding: "1rem",
	width: "100%",
	maxWidth: "28rem",
}));

const TransactionLabel = styled(Typography)<{ txType?: string }>(({ txType }) => ({
	fontSize: "0.75rem",
	fontWeight: 600,
	color: txType === "buy" ? "#E07934" : txType === "rent" ? "#2E7D32" : "#5C6BC0",
	textTransform: "uppercase",
	display: "flex",
	alignItems: "center",
	gap: "0.25rem",
}));

const SpecsRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.75rem",
	flexWrap: "wrap",
	marginTop: "0.5rem",
	color: "#616161",
	fontSize: "0.813rem",
}));

const InfoText = styled(Typography)(() => ({
	fontSize: "0.813rem",
	color: "#616161",
	display: "flex",
	alignItems: "center",
	gap: "0.5rem",
	marginTop: "0.5rem",
}));

/* Helper */

const getTransactionLabel = (type: string): string => {
	const map: Record<string, string> = { buy: "SEEKING TO BUY", rent: "SEEKING TO RENT", jd: "SEEKING FOR JD/JV", jv: "SEEKING FOR JD/JV" };
	return map[type] || type.toUpperCase();
};

/* Component */

const SubmissionSuccessModal = ({
	formData,
	onDone,
}: {
	formData: any;
	onDone: () => void;
}) => {
	const locationNames = formData.locations
		.map((loc: any) => loc.location_title || loc.locality)
		.filter((name: string) => name)
		.join(", ");

	const title = [
		formData.property_type?.[0] || formData.property_category || "",
		locationNames ? "in " + locationNames : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<Container>
			<CheckCircle sx={{ fontSize: "3rem", color: "#2E7D32" }} />

			<Typography sx={{ fontSize: "1.25rem", fontWeight: 600, textAlign: "center" }}>
				Requirement submitted for verification
			</Typography>

			<SummaryCard>
				<TransactionLabel txType={formData.transaction_type}>
					{getTransactionLabel(formData.transaction_type)}
				</TransactionLabel>
				<Typography sx={{ fontSize: "0.938rem", fontWeight: 500, marginTop: "0.5rem" }}>
					{title || "Property Requirement"}
				</Typography>
				<SpecsRow>
					{formData.budget.max > 0 && <span>₹ {formData.budget.max.toLocaleString("en-IN")}</span>}
					{formData.configuration.bhk?.length > 0 && (
						<span>{formData.configuration.bhk.map((b: number) => b === 0.5 ? "1 RK" : b >= 3 ? "3+ BHK" : b + " BHK").join(", ")}</span>
					)}
					{formData.configuration.area_max > 0 && (
						<span>{formData.configuration.area_max} {formData.configuration.area_unit || "sq.ft"}</span>
					)}
					{formData.configuration.possession_by && (
						<span>{formData.configuration.possession_by.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
					)}
				</SpecsRow>
			</SummaryCard>

			<InfoText>
				⚠️ Our team will contact you for verification within 24 hrs.
			</InfoText>

			<BeegruButton
				variant="contained"
				flavor="primary"
				fullWidth
				onClick={onDone}
				sx={{ maxWidth: "28rem" }}
			>
				Done
			</BeegruButton>
		</Container>
	);
};

export default SubmissionSuccessModal;
